import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Card,
  CardContent,
  Button,
  FormControl,
  FormLabel,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom"; // ← add useParams

import { getAccessToken, useAuth } from "../../context/auth";
import { GetVehicleList } from "../../services/api/vehicle-service";

//Components
import Header, { HeaderType } from "../../components/Header";
import type { VehicleResponse } from "../../services/model/vehicleResponse";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface VehicleListProps {
  style?: any;
  loading: boolean;
}

const VehicleList: React.FC<VehicleListProps> = (_props) => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const [, setLabelWidth] = React.useState(0);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>(); // ← tenant
  const tpath = (p: string) => (tenant ? `/${tenant}/${p}` : `/${p}`); // ← helper

  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [vehicles, setVehicles] = useState<VehicleResponse[]>([]);
  let [loading, setLoading] = useState(false);

  const ViewVehicle = (vehicleId: string) => {
    localStorage.setItem("viewVehicleId", JSON.stringify(vehicleId));
    localStorage.setItem("vehicleReturnTo", "vehiclelist");
    navigate(tpath("vehicleAddEdit")); // ← was "/vehicle"
  };

  const goToVehicleAdd = () => {
    localStorage.removeItem("viewVehicleId");
    localStorage.setItem("vehicleReturnTo", "vehiclelist");
    navigate(tpath("vehicleAddEdit")); // ← was "/vehicleAddEdit"
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title: title,
      description: message,
    });
  };

  const fetchVehicles = async () => {
    localStorage.removeItem("viewVehicleId");
    setLoading(true);
    try {
      const responseList = await GetVehicleList(token);
      if (responseList) {
        const response = responseList.data as any as VehicleResponse[];
        if (response) {
          const arraySize: number = response.length;
          let filledArray = new Array<VehicleResponse>(arraySize);

          for (let i = 0; i < response.length; i++) {
            const licensePlateNumber = response[i][
              "licensePlateNumber"
            ] as string;
            const id = response[i]["id"] as string;
            const make = response[i]["make"] as string;
            const model = response[i]["model"] as string;
            const color = response[i]["color"] as string;

            const selectOption: VehicleResponse = {
              licensePlateNumber: licensePlateNumber,
              id: id,
              make: make,
              model: model,
              color: color,
            };

            filledArray.push(selectOption);
          }
          setVehicles(filledArray);
          setLoading(false);
        }
      } else {
        setLoading(false);
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) {
        console.error(err.message);
      }
    }
  };

  useEffect(() => {
    fetchVehicles();
    setLabelWidth(inputLabel.current!.offsetWidth);
  }, []);

  let NoRecord;
  if (vehicles.length <= 0) {
    NoRecord = (
      <div>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <DirectionsCarIcon style={{ fontSize: 70 }} />
          <br />
          <Typography variant="subtitle2">
            There are no vehicles to display,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;please add a new vehicle.
          </Typography>
        </Grid>
      </div>
    );
  } else {
    NoRecord = <></>;
  }

  return (
    <div>
      <>
        <Header type={HeaderType.VehicleList} />
        <Container maxWidth="sm">
          <Grid container spacing={1}>
            <Grid size={12}>
              <Card variant="outlined" style={{ padding: "15px" }}>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  spacing={1}
                >
                  <Grid size={12}>
                    <br />
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Button
                        value="addNew"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={goToVehicleAdd} // ← unchanged call, tenant-aware inside
                      >
                        <strong>Add New</strong>
                      </Button>
                    </Grid>
                    <br />
                    <Grid size={12}>
                      <FormControl variant="outlined" style={{ width: "100%" }}>
                        <FormLabel
                          ref={inputLabel}
                          htmlFor="demo-simple-select-outlined-label"
                        ></FormLabel>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      {vehicles.map((vehicle) => (
                        <Grid key={vehicle["id"]} size={12}>
                          <Card
                            variant="outlined"
                            style={{ padding: "15px", background: "lightgrey" }}
                            onClick={() => ViewVehicle(vehicle.id)}
                          >
                            <Grid
                              container
                              justifyContent="center"
                              alignItems="center"
                              direction="column"
                            >
                              <Typography variant="subtitle1">
                                <strong>
                                  {" "}
                                  {vehicle["licensePlateNumber"]}
                                </strong>
                              </Typography>
                            </Grid>
                          </Card>
                          <Card
                            variant="outlined"
                            style={{ padding: "15px" }}
                            onClick={() => ViewVehicle(vehicle.id)}
                          >
                            <CardContent>
                              <Typography variant="subtitle2">
                                <strong>Make: </strong> {vehicle["make"]}
                              </Typography>
                              <ArrowForwardIosIcon
                                fontSize="medium"
                                className="arrow_navigate"
                              />
                              <Typography variant="subtitle2">
                                <strong>Model: </strong> {vehicle["model"]}
                              </Typography>
                              <Typography variant="subtitle2">
                                <strong>Color: </strong> {vehicle["color"]}
                              </Typography>
                            </CardContent>
                          </Card>
                          <br />
                        </Grid>
                      ))}
                      {NoRecord}
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </>
      <BrandLoader open={loading} />
    </div>
  );
};

export default VehicleList;
